import _ from 'lodash'
import _isFinite from 'lodash/isFinite'
import moment from 'moment'
import numeral from 'numeral'

// [ {...}, ... ] -> { [array[i][objectKey]]: defaultValue || array[i], ... }
export const arrayOfObjectsToObject = (array, objectKey, defaultValue) =>
  array.reduce((prev, curr) => ({ ...prev, [curr[objectKey]]: defaultValue || curr }), {})

export const arrayOfObjectsToObjectsOfArraysByKey = (array, objectKey) =>
  array.reduce(
    (prev, curr) =>
      prev[curr[objectKey]]
        ? { ...prev, [curr[objectKey]]: [...prev[curr[objectKey]], curr] }
        : { ...prev, [curr[objectKey]]: [curr] },
    {}
  )

export const checkAllObjectValuesMatchExpectations = (object, expectation) =>
  !!Object.keys(object).filter((elem) => object[elem] === expectation).length

export const sum = (a, b) => a + b

export const alphabeticalSort = (arr, property) =>
  arr.sort((a, b) => {
    const labelA = a[property].toLowerCase()
    const labelB = b[property].toLowerCase()

    if (labelA > labelB) return 1
    else if (labelA < labelB) return -1
    else return 0
  })

const padZero = (number) => {
  if (_isFinite(number)) {
    return number.toString().length === 1 ? `0${number}` : number
  }
  return '00'
}

export const cutArrayToTopAndBottom = (array) => {
  const countPerSubtable = Math.min(10, array.length) / 2

  return {
    top: array.slice(0, countPerSubtable),
    bottom: array.slice(array.length - countPerSubtable),
  }
}

export const formatSecondsToDuration = (durationSeconds) => {
  const seconds = durationSeconds % 60
  const minutes = Math.floor((durationSeconds / 60) % 60)
  const hours = Math.floor(durationSeconds / (60 * 60))

  return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`
}

export const timeStringToSeconds = (timeString) => {
  let result = 0
  try {
    let m = timeString.match(hours)
    if (m) {
      result += parseInt(m) * 60 * 60
    }
    m = timeString.match(minutes)
    if (m) {
      result += parseInt(m) * 60
    }
    m = timeString.match(seconds)
    if (m) {
      result += parseInt(m)
    }
  } catch (e) {
    return 0
  }

  return result
}

export const normalizePercent = (p) => Math.round(p * 100 * 100) / 100

export const normalizeReportData = (reportData) => {
  reportData.reportsNum = reportData.reportsNum.map((n) => (n === 'N/A' ? 0 : n))
  reportData.mitigationTime = reportData.mitigationTime.map((t) =>
    t === 'No Mitigation' ? 0 : timeStringToSeconds(t)
  )
  reportData.firstReport = reportData.firstReport.map((t) =>
    t === 'No Reports' ? 0 : typeof t === 'string' ? timeStringToSeconds(t) : t
  )

  // put all more than 5 times clicks in the 5 times bucket.
  const clkFreq = reportData.clickFrequency
  reportData.clickFrequency = Object.keys(clkFreq).reduce((acc, key) => {
    if (key <= 5) {
      acc[key] = clkFreq[key]
    } else {
      acc[5] = acc[5] ? acc[5].concat(clkFreq[key]) : clkFreq[key]
    }
    return acc
  }, {})

  return reportData
}

const hours = /(\d+)h/g
const minutes = /(\d+)m/g
const seconds = /(\d+)s/g

export const momentize = (campaigns) =>
  campaigns.map((c) => ({
    ...c,
    scheduleTime: moment(c.scheduleTime),
    closeTime: moment(c.closeTime),
  }))

export const formatNumber = (value, zeroAs = '0') =>
  zeroAs && value == 0 ? zeroAs : numeral(value).format('0,0')

const URI_PREFIX = 'data:text/csv;charset=utf-8,%EF%BB%BF'

export const objects2csvuri = (array, props, titles) => {
  let content = titles ? `${titles.join(',')}\n` : `${props.join(',')}\n`
  content += array.map((item) => props.map((prop) => item[prop]).join(',')).join('\n')

  return URI_PREFIX + encodeURIComponent(content)
}

export const showShortAddress = (address) => {
  if (!address) return ''
  if (address.length < 40) return address
  const maxPartLength = 30
  let localPart
  let domain
  const splited = address.split(/(?=@)/g)
  switch (splited.length) {
    case 1: {
      const res = splited[0]
      if (res.split('.')[0].length > maxPartLength) {
        return `${res.split('.')[0].substring(0, 27)}...${res
          .split('.')
          .filter((e, i) => i !== 0)
          .join('.')}`
      }
      return res
    }
    case 2:
    case 3:
    case 4:
    case 5: {
      localPart = splited[0]
      domain = address
        .split(/(?=@)/g)
        .filter((e, i) => i !== 0)
        .join('')
      if (localPart.length > maxPartLength) {
        localPart = `${localPart.substring(0, 27)}...`
      }
      domain = domain
        .split('.')
        .map((elem) => {
          if (elem.length > maxPartLength) return `${elem.substring(0, 27)}...`
          return elem
        })
        .join('.')
      return localPart + domain
    }
    default:
      return address
  }
}

export const serverAddress = (host, ip) => `${host || ''}${ip ? ` (${ip})` : ''}`

export function xhrExtractFileName(xhr) {
  let filename = ''
  const disposition = xhr.getResponseHeader('Content-Disposition')
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(disposition)
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '')
    }
  }
  return filename
}

export const wrapCSVTextToBlob = (CSVText) => new Blob(['\ufeff', CSVText])

export function debounce(func, wait, immediate) {
  let timeout
  const cb = function () {
    const context = this
    const args = arguments
    const later = function () {
      timeout = null
      if (!immediate) {
        func.apply(context, args)
      }
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait || 200)
    if (callNow) {
      func.apply(context, args)
    }
  }
  cb.cancel = function cancel() {
    clearTimeout(timeout)
  }
  return cb
}

export function genId(length = 5) {
  return Math.random().toString(36).substring(length)
}

/**
 *
 * @param {Object} o - Object to search in
 * @param {String} fieldName - Name to check
 * @param {Any} value - Value to compare
 * @param {Array} results (optional)
 *
 * Function is searching in nested object by fieldName eq value
 * and returning first object which matched conditional.
 *
 * @param {Array} results when specified function is returning all occurrences.
 */
export function findInNestedObject(o, fieldName, value, results) {
  // Early return
  if (o[fieldName] === value) {
    if (results) {
      results.push(o)
    } else {
      return o
    }
  }
  let result
  let p
  for (p in o) {
    if (o.hasOwnProperty(p) && typeof o[p] === 'object' && o[p] !== null) {
      result = findInNestedObject(o[p], fieldName, value, results)
      if (result) {
        return result
      }
    }
  }
  return result
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function capitalizeThreatType(string) {
  const map = {
    'Qr Code Attack': 'QR Code Attack',
    'qr code Attack': 'QR Code Attack',
    'qr code attack': 'QR Code Attack',
    'Vip Impersonation': 'VIP Impersonation',
    'Advance-Fee Scam': 'Advance-fee Scam',
    'advance-fee scam': 'Advance-fee Scam',
    'Advance-fee scam': 'Advance-fee Scam',
    'Advance-fee Scam': 'Advance-fee Scam',
    'vip impersonation': 'VIP Impersonation',
  }
  return (
    map[string] ||
    string
      .split(' ')
      .map((word) => {
        // Handle hyphenated words
        if (word.includes('-')) {
          return word.split('-').map(capitalizeFirstLetter).join('-')
        }
        return capitalizeFirstLetter(word)
      })
      .join(' ')
  )
}

export function containsSpace(str) {
  return /\s/.test(str)
}

// recursively beautify all object keys to valid camelCase variables
export const beautifyObjectKeys = (obj) =>
  _.transform(obj, (acc, value, key, target) => {
    const camelKey = _.isArray(target) ? key : _.camelCase(key)
    acc[camelKey] = _.isObject(value) ? beautifyObjectKeys(value) : value
  })

/**
 * Combines the region and user/company ID to create a combined ID.
 * If the environment is 'STAGING', it prefixes the combined ID with 'testEnv-'.
 *
 * @param {string} environment - The environment (App/selectors:selectEnvironment).
 * @param {string} region - The region of the company (App/selectors:selectRegion).
 * @param {string} id - The ID of user/company
 * (App/selectors:selectUserId, App/selectors:select(Self|Actual)CompanyId).
 * @returns {string} - The combined user/company ID.
 */
export const getCombinedId = (environment, region, id) => {
  const regionWithId = `${region}-${id}`
  if (environment === 'STAGING') {
    return `testEnv-${regionWithId}`
  } else return regionWithId
}

/**
 * Removes excess whitespace from a string by replacing consecutive whitespace characters with a single space character,
 * and removes any leading or trailing whitespace.
 *
 * @param {string} s - The string to clean up.
 * @returns {string} - The cleaned-up string.
 */
export const clearString = (s) => s.replace(/\s+/g, ' ').trim()

/**
 * Returns a filter function for searching user data based on a given search term.
 *
 * @param {string} searchTerm - The search term to use for filtering user data.
 * @param {string} [emailField='email'] - The name of the email field in the user data object.
 * @param {string} [firstNameField='first_name'] - The name of the first name field in the user data object.
 * @param {string} [lastNameField='last_name'] - The name of the last name field in the user data object.
 * @returns {Function} - A filter function that takes user data as input and returns true if the data matches the search term.
 */
export const isUserDataContainsSearchTermFilter = (
  searchTerm,
  emailField = 'email',
  firstNameField = 'first_name',
  lastNameField = 'last_name'
) => {
  const _searchTerm = clearString(searchTerm.toLowerCase())
  return (userData) =>
    userData?.[emailField].toLowerCase().includes(_searchTerm) ||
    _searchTerm.split(' ').every(
      (term) =>
        // full name
        `${userData?.[firstNameField] || ''} ${userData?.[lastNameField] || ''}`
          .toLowerCase()
          .includes(term) ||
        // reversed name
        `${userData?.[firstNameField] || ''} ${userData?.[lastNameField] || ''}`
          .toLowerCase()
          .includes(term)
    )
}

export const getContentCopyName = (originalName) => {
  const timestamp = +new Date()
  return `${originalName} (copy ${timestamp})`
}

/**
 * Returns the short name representation of a language based on the ISO language code.
 * @param {string} isoCode - The ISO language code, such as "pt" or "en-US".
 * @returns {string|null} The short name representation of the language, e.g., "PT" or "EN (US)",
 *                        or null if the input is empty.
 */
export const getFormattedLanguageShortName = (isoCode) => {
  if (isoCode) {
    const [languageCode, countryCode] = isoCode.split('-')
    return `${languageCode.toUpperCase()}${countryCode ? ` (${countryCode.toUpperCase()})` : ''}`
  }
  return null
}

/**
 * Returns an object without undefined values.
 * @param {object} objectIn - The object with values example: { name: 'shahar', age: undefined }".
 * @returns {object} - The object is returned without undefined values example: { name: 'shahar' }
 */
export const removeUndefinedValues = (objectIn) =>
  Object.keys(objectIn).reduce(
    (prev, curr) =>
      objectIn[curr] !== undefined
        ? {
            ...prev,
            [curr]: objectIn[curr],
          }
        : prev,
    {}
  )

export const mapToLabels = (array) => array?.map((elem) => elem.label)
